// Les séances suivent le planning général des salles (D-135), logique pure : ce que « Programmer » propose (les
// créneaux de l'équipe, les créneaux libres à demander, ceux libérés cette semaine, les créneaux en plus, « Ailleurs »),
// ce qu'un créneau autorise (raccourcir, jamais déborder), la ligne de chaque séance à mettre en cohérence (ce qui ne
// colle pas, « Rattacher » ou « Marquer Ailleurs »), et ce que fait un changement d'équipe sur un créneau (la fiche
// « Un créneau du club »). La base garde la même règle (migration 20261104000000_seances_creneaux.sql).
import { LongDate, WEEKDAYS, clock, hr, longDate, periodOfDay, seasonOf, sportHalls, toMin, type Board, type BSlot } from './creneaux';
import { addDays, isoDay, withArticle } from './salles';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const overlap = (a0: number, a1: number, b0: number, b1: number) => a0 < b1 && b0 < a1;

// --- « Programmer » : quel créneau ------------------------------------------------------------------------------------
export type ChoiceKind = 'team' | 'free' | 'freed' | 'extra' | 'hall' | 'ailleurs';
export interface Choice {
  key: string; kind: ChoiceKind; title: string; sub: string;
  hallId: string | null;
  /** Un créneau de la semaine (le sien, un libre) : son jour (1 = lundi). */
  weekday: number | null;
  /** Une fois : le jour (créneau libéré), ou le premier jour possible (créneau en plus). */
  day: string | null; dayMax: string | null;
  /** Les bornes du créneau (« 16:00 », « 17:30 ») ; vides : horaires libres (« Ailleurs », une salle sans créneaux). */
  start: string | null; end: string | null;
  slotId?: string; extraId?: string;
  /** Un créneau libre déjà demandé par moi (en attente). */
  pending?: boolean;
  /** Le créneau de l'équipe porte déjà sa séance de chaque semaine. */
  habit?: boolean;
  /** Créneau libéré : l'équipe qui l'a libéré. */
  from?: string;
}

/** « Karim (responsable des salles) », sinon « le bureau ». */
export function respName(b: Pick<Board, 'resp'>): string {
  if (!b.resp?.names?.length) return 'le bureau';
  return `${b.resp.names.join(' et ')} (${b.resp.title.charAt(0).toLowerCase()}${b.resp.title.slice(1)})`;
}
/** Le prénom seul (« Karim »), sinon « le bureau ». */
export const respFirstName = (b: Pick<Board, 'resp'>) => (b.resp?.names?.length ? b.resp.names.join(' et ') : 'le bureau');

const hallName = (b: Board, id: string | null) => b.halls.find(x => x.id === id)?.name ?? 'Salle';
/** Les créneaux de la saison du jour. */
export const seasonSlots = (b: Board) => b.slots.filter(s => s.season === seasonOf(b.today));
/** Le club a-t-il un planning général cette saison (au moins un créneau) ? Sinon, « Programmer » reste comme avant. */
export const hasPlan = (b: Board) => seasonSlots(b).length > 0;

/** La séance habituelle de l'équipe est-elle déjà dans ce créneau (une série qui y a lieu ce jour-là) ? */
function habitIn(b: Board, teamId: string, s: BSlot, exceptEvent: string | null = null): boolean {
  return b.occ.some(o => o.team_id === teamId && o.event_id !== exceptEvent && !o.one_off && o.kind !== 'match' && o.hall_id === s.hall_id && isoDay(o.day) === s.weekday
    && overlap(toMin(clock(o.starts)), toMin(clock(o.ends)), toMin(s.starts), toMin(s.ends)));
}

/** Ce que « Programmer » propose pour une équipe : ses créneaux, les créneaux libres (une demande au responsable des salles),
 *  ceux libérés cette semaine, ses créneaux en plus, les salles sans créneaux, puis « Ailleurs ». Une équipe sans créneau :
 *  seulement les libres et « Ailleurs » (D-135). Un match amical à domicile : pas d'« Ailleurs » (c'est « À l'extérieur »). */
export function choicesFor(b: Board, teamId: string, now: Date, o: { match?: boolean } = {}): Choice[] {
  const team = b.teams.find(t => t.id === teamId);
  const slots = seasonSlots(b).sort((x, y) => x.weekday - y.weekday || x.starts.localeCompare(y.starts));
  const mine = slots.filter(s => s.team_id === teamId);
  const out: Choice[] = [];
  for (const s of mine) {
    out.push({ key: `t:${s.id}`, kind: 'team', title: `${cap(WEEKDAYS[s.weekday - 1]!)} ${hr(s.starts, s.ends)} · ${hallName(b, s.hall_id)}`,
      sub: `Créneau des ${team?.name ?? 'l’équipe'}`, hallId: s.hall_id, weekday: s.weekday, day: null, dayMax: null,
      start: s.starts.slice(0, 5), end: s.ends.slice(0, 5), slotId: s.id, habit: habitIn(b, teamId, s) });
  }
  for (const s of slots.filter(x => !x.team_id)) {
    const pending = b.asks.some(a => a.slot_id === s.id && a.status === 'pending' && a.team_id === teamId);
    out.push({ key: `f:${s.id}`, kind: 'free', title: `${cap(WEEKDAYS[s.weekday - 1]!)} ${hr(s.starts, s.ends)} · ${hallName(b, s.hall_id)}`,
      sub: pending ? `Ta demande est partie · ${respFirstName(b)} répond` : b.manage ? 'Libre · tu le donnes à l’équipe' : `Libre · une demande part à ${respName(b)}`,
      hallId: s.hall_id, weekday: s.weekday, day: null, dayMax: null, start: s.starts.slice(0, 5), end: s.ends.slice(0, 5), slotId: s.id, pending });
  }
  if (mine.length) {
    // Libérés cette semaine : la séance annulée d'une autre équipe, que rien d'autre n'occupe, dans les 7 jours.
    const until = addDays(b.today, 6);
    for (const x of b.occ.filter(y => y.cancelled && y.kind !== 'match' && y.team_id !== teamId && y.day >= b.today && y.day <= until && new Date(y.starts) > now)) {
      const a = toMin(clock(x.starts)), z = toMin(clock(x.ends));
      if (b.occ.some(y => !y.cancelled && y.hall_id === x.hall_id && y.day === x.day && overlap(a, z, toMin(clock(y.starts)), toMin(clock(y.ends))))) continue;
      const by = b.teams.find(t => t.id === x.team_id)?.name ?? 'une autre équipe';
      out.push({ key: `l:${x.event_id}:${x.day}`, kind: 'freed', title: `${LongDate(x.day)} ${hr(clock(x.starts), clock(x.ends))} · ${hallName(b, x.hall_id)}`,
        sub: `Libéré par les ${by} · cette semaine seulement`, hallId: x.hall_id, weekday: null, day: x.day, dayMax: x.day,
        start: clock(x.starts), end: clock(x.ends), from: by });
    }
    for (const e of b.extras.filter(x => x.for_mode === 'team' && x.team_id === teamId && x.to_day >= b.today)) {
      const first = e.from_day < b.today ? b.today : e.from_day;
      out.push({ key: `e:${e.id}`, kind: 'extra', title: `${first === e.to_day ? LongDate(first) : `Du ${longDate(first)} au ${longDate(e.to_day)}`} ${hr(e.starts, e.ends)} · ${hallName(b, e.hall_id)}`,
        sub: `Créneau en plus pour les ${team?.name ?? 'l’équipe'}${e.note ? ` · ${e.note}` : ''}`, hallId: e.hall_id, weekday: null, day: first, dayMax: e.to_day,
        start: e.starts.slice(0, 5), end: e.ends.slice(0, 5), extraId: e.id });
    }
  }
  // Une salle de sport sans aucun créneau du club : hors du planning général, horaires libres.
  for (const x of sportHalls(b).filter(y => !slots.some(s => s.hall_id === y.id))) {
    out.push({ key: `h:${x.id}`, kind: 'hall', title: x.name, sub: 'Sans créneaux du club : horaires libres', hallId: x.id, weekday: null, day: null, dayMax: null, start: null, end: null });
  }
  if (!o.match) out.push({ key: 'ailleurs', kind: 'ailleurs', title: 'Ailleurs (hors salles du club)', sub: 'Un footing, un amical chez un autre club…', hallId: null, weekday: null,
    day: null, dayMax: null, start: null, end: null });
  return out;
}

/** Le choix par défaut : le premier créneau de l'équipe qui n'a pas encore sa séance, sinon son premier créneau, sinon
 *  « Ailleurs » (jamais une demande d'office). */
export function defaultChoice(list: Choice[]): Choice | null {
  return list.find(c => c.kind === 'team' && !c.habit) ?? list.find(c => c.kind === 'team') ?? list.find(c => c.kind === 'ailleurs') ?? list[0] ?? null;
}

/** Le premier jour possible d'un créneau de la semaine : aujourd'hui s'il n'a pas commencé, sinon le prochain. */
export function nextDay(c: Pick<Choice, 'weekday' | 'day' | 'start'>, today: string, now: Date): string {
  if (c.day) return c.day;
  if (!c.weekday) return today;
  let d = today;
  while (isoDay(d) !== c.weekday) d = addDays(d, 1);
  if (d === today && c.start && toMin(c.start) <= now.getHours() * 60 + now.getMinutes()) d = addDays(d, 7);
  return d;
}

/** La phrase sous les heures (maquette p1). */
export function choiceHint(c: Choice, b: Board): string {
  const day = c.weekday ? WEEKDAYS[c.weekday - 1] : null;
  switch (c.kind) {
    case 'team': return `Dans le créneau : tu peux raccourcir (${hr(c.start, c.end)} au plus). Un autre horaire : demande un créneau libre.${c.habit ? ` Ta séance de chaque ${day} y est déjà.` : ''}`;
    case 'free': return c.pending ? `Ta demande est partie : ${respFirstName(b)} accepte ou refuse.`
      : b.manage ? `Dans le créneau (${hr(c.start, c.end)} au plus) : tu le donnes à l’équipe, c’est posé tout de suite.`
      : `Dans le créneau (${hr(c.start, c.end)} au plus). ${cap(respFirstName(b))} accepte ou refuse ; accepté, c’est posé dans le planning de l’équipe.`;
    case 'freed': return `Cette semaine seulement : ${hr(c.start, c.end)} au plus. Le coach des ${c.from} est prévenu.`;
    case 'extra': return `Dans le créneau en plus : ${hr(c.start, c.end)} au plus.`;
    case 'hall': return 'Cette salle n’a pas de créneaux du club : les horaires sont libres.';
    default: return 'Le responsable des salles en est informé.';
  }
}

/** Ce que vérifie le formulaire avant d'envoyer (la base vérifie la même chose) : rien, ou ce qui ne va pas. */
export function checkChoice(c: Choice, b: Board, v: { day: string; start: string; end: string; weekly: boolean; now: Date; place?: string }): string | null {
  if (!v.day || !v.start || !v.end) return 'Indique la date et les heures.';
  if (v.end <= v.start) return 'La fin doit être après le début.';
  if (v.day < b.today) return 'Choisis un jour à venir.';
  if (c.kind === 'ailleurs') return v.place?.trim() ? null : 'Indique le lieu.';
  if (c.start && c.end && (v.start < c.start || v.end > c.end)) return `Dans le créneau, s’il te plaît : ${hr(c.start, c.end)} au plus.`;
  if (c.weekday && isoDay(v.day) !== c.weekday) return `Ce créneau est le ${WEEKDAYS[c.weekday - 1]}.`;
  if (c.day && (v.day < c.day || v.day > (c.dayMax ?? c.day))) return c.dayMax && c.dayMax !== c.day ? `Entre le ${longDate(c.day)} et le ${longDate(c.dayMax)}.` : `C’est le ${longDate(c.day)}.`;
  if (c.kind === 'team' && v.weekly && c.habit) return `Ta séance de chaque ${WEEKDAYS[c.weekday! - 1]} est déjà dans ce créneau.`;
  if (c.kind === 'free' && c.pending) return 'Une demande est déjà en cours pour ce créneau.';
  if (c.kind === 'team' && !v.weekly) {
    const p = periodOfDay(b, v.day);
    if (p.mode === 'apart' || p.mode === 'none') return 'Pendant ces vacances, les créneaux changent : passe par tes souhaits de vacances (Les salles).';
  }
  return null;
}

// --- La mise en cohérence -----------------------------------------------------------------------------------------
export interface PlanItemLike {
  event_id: string; team_id: string; kind: string; title: string; hall_id: string | null; location: string | null;
  weekly: boolean; weekday: number; day: string; starts: string; ends: string; coach: string | null; asked: { at: string; by: string | null } | null;
}
export type CohAction = { kind: 'attach'; slotId: string; label: string } | { kind: 'mark'; label: string };
export interface CohRow { item: PlanItemLike; head: string; where: string; sub: string; action: CohAction; canAsk: boolean; asked: string | null }

/** Une ligne par séance : ce qui ne colle pas, et le geste proposé (rattacher à un créneau de l'équipe ce jour-là, sinon
 *  « Ailleurs »). */
export function cohRows(b: Board, items: PlanItemLike[]): CohRow[] {
  const slots = seasonSlots(b);
  return items.map(it => {
    const team = b.teams.find(t => t.id === it.team_id);
    const day = WEEKDAYS[it.weekday - 1]!;
    const when = it.weekly ? `${day} ${hr(it.starts, it.ends)}` : `${longDate(it.day)} ${hr(it.starts, it.ends)}`;
    const hall = it.hall_id ? b.halls.find(x => x.id === it.hall_id) ?? null : null;
    const where = hall?.name ?? (it.location ? it.location : 'lieu à préciser');
    // Ses créneaux ce jour-là, sauf ceux qui portent déjà sa séance habituelle (une autre que celle-ci).
    const mineDay = slots.filter(s => s.team_id === it.team_id && s.weekday === it.weekday && !habitIn(b, it.team_id, s, it.event_id))
      .sort((x, y) => x.starts.localeCompare(y.starts));
    const sameHall = mineDay.filter(s => s.hall_id === it.hall_id);
    const a = toMin(it.starts), z = toMin(it.ends);
    const near = (s: BSlot) => Math.abs(toMin(s.starts) - a);
    let sub: string; let action: CohAction;
    if (sameHall.length) {
      const s = [...sameHall].sort((x, y) => near(x) - near(y))[0]!;
      sub = `Leur créneau est ${day} ${hr(s.starts, s.ends)} dans cette salle.`;
      action = { kind: 'attach', slotId: s.id, label: `Rattacher : ${day} ${hr(s.starts, s.ends)}` };
    } else if (mineDay.length) {
      const s = [...mineDay].sort((x, y) => near(x) - near(y))[0]!;
      const name = hallName(b, s.hall_id);
      sub = `Le créneau du ${day} est ${au(name)} (${hr(s.starts, s.ends)}).`;
      action = { kind: 'attach', slotId: s.id, label: `Rattacher : ${name}` };
    } else {
      action = { kind: 'mark', label: 'Marquer « Ailleurs »' };
      const inHall = slots.filter(s => s.hall_id === it.hall_id && s.weekday === it.weekday);
      const other = inHall.find(s => s.team_id && toMin(s.starts) <= a && toMin(s.ends) >= z) ?? inHall.find(s => s.team_id && overlap(a, z, toMin(s.starts), toMin(s.ends)));
      const free = inHall.find(s => !s.team_id && overlap(a, z, toMin(s.starts), toMin(s.ends)));
      if (!hall) sub = it.location ? `« ${it.location} » n’est pas une salle du club.` : 'Pas de salle du club.';
      else if (!inHall.length) sub = `${cap(withArticle(hall.name))} n’a pas de créneau du club le ${day}.`;
      else if (other) sub = `Ce créneau est celui des ${b.teams.find(t => t.id === other.team_id)?.name ?? 'une autre équipe'}.`;
      else if (free) sub = `Le créneau ${hr(free.starts, free.ends)} est libre : le coach peut le demander.`;
      else sub = `Pas de créneau du club à cette heure-là le ${day}.`;
    }
    const asked = it.asked ? `Demandé à ${it.coach ?? 'son coach'} le ${longDate(it.asked.at.slice(0, 10))}` : null;
    return { item: it, head: `${team?.name ?? 'Équipe'} · ${when}`, where, sub, action, canAsk: !!it.coach && !it.asked, asked };
  });
}
/** « au Gymnase de Rivebelle », « à la Salle Bellevue ». */
export function au(name: string): string {
  const w = withArticle(name);
  return w.startsWith('le ') ? `au ${name}` : w.startsWith('la ') ? `à la ${name}` : w.startsWith('l’') ? `à l’${name}` : `à ${name}`;
}

/** La ligne « À faire » des salles (D-138 : « N séances hors créneau »). */
export const cohTodo = (n: number) => ({ key: 'coh', href: '#/salles/coherence', title: 'Mise en cohérence', short: 'la mise en cohérence',
  sub: `${n} séance${n > 1 ? 's' : ''} ne ${n > 1 ? 'collent' : 'colle'} à aucun créneau` });

// --- Un créneau du club : ce que fait le changement d'équipe (maquette p2) -----------------------------------------
export interface EffectText { head: string; body: string; old: string | null }
export function effectText(e: { from?: string; weekday?: number; hall?: string; starts?: string; ends?: string;
  old?: { name: string; coach: string | null; stop: boolean } | null;
  team?: { name: string; coach: string | null; action: string; before: { hall: string | null; starts: string; ends: string } | null } | null },
  players: string, warned: string): EffectText | null {
  if (!e.from || !e.weekday) return null;
  const day = WEEKDAYS[e.weekday - 1]!;
  const told = e.team ? `${e.team.coach ? `${e.team.coach} (coach) et les ${players} sont ${warned}` : `Les ${players} sont ${warned}`}.` : '';
  const old = e.old ? (e.old.stop ? `Les ${e.old.name} : leur séance du ${day} s’arrête dès le ${longDate(e.from)}, ${e.old.coach ? `${e.old.coach} est prévenu` : 'leur coach est prévenu'}.`
    : `Les ${e.old.name} n’avaient pas de séance sur ce créneau.`) : null;
  if (!e.team) return { head: 'Le créneau redevient libre', body: 'Les coachs le voient dans l’agenda et peuvent le demander.', old };
  const slot = `${day} ${hr(e.starts, e.ends)}, ${e.hall}`;
  const b = e.team.before;
  switch (e.team.action) {
    case 'keep': return { head: `La séance des ${e.team.name} est déjà là`, body: `Le ${day} ${hr(b?.starts, b?.ends)} : elle reste telle quelle.`, old };
    case 'adjust':
    case 'follow': return { head: `La séance des ${e.team.name} suit ce créneau`,
      body: `Dès le ${longDate(e.from)} : ${slot}. ${told} Avant : le ${day} ${hr(b?.starts, b?.ends)}${b?.hall && b.hall !== e.hall ? ` (${b.hall})` : ''}.`, old };
    default: return { head: `La séance des ${e.team.name} est posée`, body: `Dès le ${longDate(e.from)} : chaque ${slot}. ${told}`, old };
  }
}
